/* =============================================================
   PDP INGREDIENTS BLOCK — pdp-ingredients.js
   Sachi Skin · Ingredient Block v2

   Responsibilities:
   1. Scroll-entrance IntersectionObserver (left col + cards)
   2. Panel switcher (exit → stagger-in + footer fade)
   3. Carousel dot sync (native scroll → rAF debounce)
   4. Panel swipe gesture (touch hijack only at carousel edges)
   5. Mobile product selector (counter, name, pips, prev/next)
   ============================================================= */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────
     UTILITIES
  ──────────────────────────────────────────────────────── */
  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ─────────────────────────────────────────────────────
     1. SCROLL ENTRANCE
  ──────────────────────────────────────────────────────── */
  function staggerCards(cards, baseDelayMs) {
    if (prefersReducedMotion()) {
      cards.forEach(function (c) {
        c.classList.add('is-visible');
        c.style.transitionDelay = '0ms';
      });
      return;
    }
    cards.forEach(function (card, i) {
      card.style.transitionDelay = baseDelayMs + i * 65 + 'ms';
      void card.offsetWidth; /* force reflow so delay is read */
      card.classList.add('is-visible');
    });
  }

  function resetCards(cards) {
    cards.forEach(function (card) {
      card.style.transitionDelay = '0ms';
      card.classList.remove('is-visible');
    });
  }

  function initEntranceObserver(section) {
    var left = section.querySelector('.pdp-ing__left');
    var activePanel = section.querySelector('.pdp-ing__panel.is-active');
    if (!activePanel) return;

    var initCards = activePanel.querySelectorAll('.pdp-ing-card');

    if (prefersReducedMotion()) {
      if (left) left.classList.add('is-visible');
      initCards.forEach(function (c) { c.classList.add('is-visible'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;

        if (el === left) {
          el.classList.add('is-visible');
        } else if (el.classList.contains('pdp-ing-card')) {
          staggerCards(Array.from(initCards), 80);
        }
        io.unobserve(el);
      });
    }, { threshold: 0.1 });

    if (left) io.observe(left);
    if (initCards[0]) io.observe(initCards[0]);
  }

  /* ─────────────────────────────────────────────────────
     2. PANEL SWITCHER
  ──────────────────────────────────────────────────────── */
  function switchPanel(btn, section) {
    var nextId = btn.dataset.panel;
    var current = section.querySelector('.pdp-ing__panel.is-active');
    var next = section.querySelector('#' + nextId);

    if (!next || current === next) return;

    /* ── update desktop nav ── */
    section.querySelectorAll('.pdp-ing__nav-btn').forEach(function (b) {
      b.classList.remove('is-active');
      b.setAttribute('aria-selected', 'false');
      b.setAttribute('tabindex', '-1');
    });
    btn.classList.add('is-active');
    btn.setAttribute('aria-selected', 'true');
    btn.setAttribute('tabindex', '0');

    /* ── exit animation ── */
    current.classList.add('is-exiting');

    setTimeout(function () {
      /* ── clean up exiting panel ── */
      current.classList.remove('is-active', 'is-exiting');
      resetCards(current.querySelectorAll('.pdp-ing-card'));

      /* ── prepare incoming panel ── */
      var newCards = next.querySelectorAll('.pdp-ing-card');

      resetCards(newCards);

      next.classList.add('is-active');
      next.removeAttribute('hidden');

      /* sync mobile selector */
      updateMobileNav(nextId, section);

      /* stagger new cards */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          staggerCards(Array.from(newCards), 0);
        });
      });
    }, 220);
  }

  /* ─────────────────────────────────────────────────────
     3. (CAROUSEL REMOVED — cards now flow flat in grid)
  ──────────────────────────────────────────────────────── */

  /* ─────────────────────────────────────────────────────
     4. SWIPE GESTURE — switch panel on horizontal swipe
  ──────────────────────────────────────────────────────── */
  function initPanelSwipe(section) {
    var startX = 0;
    var startY = 0;

    section.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    section.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - startX;
      var dy = e.changedTouches[0].clientY - startY;

      /* must be a horizontal-dominant swipe ≥ 50px */
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;

      var btns = Array.from(section.querySelectorAll('.pdp-ing__nav-btn'));
      var activeIdx = btns.findIndex(function (b) { return b.classList.contains('is-active'); });

      if (dx < 0 && activeIdx < btns.length - 1) {
        switchPanel(btns[activeIdx + 1], section);
      } else if (dx > 0 && activeIdx > 0) {
        switchPanel(btns[activeIdx - 1], section);
      }
    }, { passive: true });
  }

  /* ─────────────────────────────────────────────────────
     5. MOBILE PRODUCT SELECTOR
  ──────────────────────────────────────────────────────── */
  function updateMobileNav(panelId, section) {
    var btns = Array.from(section.querySelectorAll('.pdp-ing__nav-btn'));
    var idx = btns.findIndex(function (b) { return b.dataset.panel === panelId; });
    if (idx < 0) return;

    var info    = section.querySelector('.pdp-ing__mnav-info');
    var counter = section.querySelector('.pdp-ing__mnav-counter');
    var nameEl  = section.querySelector('.pdp-ing__mnav-name');
    var pips    = section.querySelectorAll('.pdp-ing__mnav-pip');
    var prev    = section.querySelector('#ing-mnav-prev');
    var next    = section.querySelector('#ing-mnav-next');

    if (info) info.classList.add('is-switching');

    setTimeout(function () {
      var total = btns.length;
      var labelEl = btns[idx].querySelector('.pdp-ing__nav-label');

      if (counter) counter.textContent = pad(idx + 1) + ' / ' + pad(total);
      if (nameEl && labelEl) nameEl.textContent = labelEl.textContent;

      pips.forEach(function (p, i) { p.classList.toggle('is-active', i === idx); });

      if (info) info.classList.remove('is-switching');
    }, 180);
  }

  function initMobileNav(section) {
    var prev = section.querySelector('#ing-mnav-prev');
    var next = section.querySelector('#ing-mnav-next');

    if (prev) {
      prev.addEventListener('click', function () {
        var btns = Array.from(section.querySelectorAll('.pdp-ing__nav-btn'));
        var ai = btns.findIndex(function (b) { return b.classList.contains('is-active'); });
        var target = ai > 0 ? btns[ai - 1] : btns[btns.length - 1];
        switchPanel(target, section);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        var btns = Array.from(section.querySelectorAll('.pdp-ing__nav-btn'));
        var ai = btns.findIndex(function (b) { return b.classList.contains('is-active'); });
        var target = ai < btns.length - 1 ? btns[ai + 1] : btns[0];
        switchPanel(target, section);
      });
    }

    /* pip buttons */
    section.querySelectorAll('.pdp-ing__mnav-pip').forEach(function (pip) {
      pip.addEventListener('click', function () {
        var panelId = pip.dataset.panel;
        var btn = section.querySelector('.pdp-ing__nav-btn[data-panel="' + panelId + '"]');
        if (btn) switchPanel(btn, section);
      });
    });
  }

  /* Keyboard arrow navigation between tabs */
  function initTabKeyboard(section) {
    var nav = section.querySelector('.pdp-ing__nav');
    if (!nav) return;

    nav.addEventListener('keydown', function (e) {
      var btns = Array.from(section.querySelectorAll('.pdp-ing__nav-btn'));
      var activeIdx = btns.findIndex(function (b) { return b === document.activeElement; });
      if (activeIdx < 0) return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        var next = btns[activeIdx + 1];
        if (next) { next.focus(); switchPanel(next, section); }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        var prev = btns[activeIdx - 1];
        if (prev) { prev.focus(); switchPanel(prev, section); }
      } else if (e.key === 'Home') {
        e.preventDefault();
        btns[0].focus(); switchPanel(btns[0], section);
      } else if (e.key === 'End') {
        e.preventDefault();
        btns[btns.length - 1].focus(); switchPanel(btns[btns.length - 1], section);
      }
    });
  }

  /* ─────────────────────────────────────────────────────
     6. PANEL HEIGHT EQUALISER
     Measures all panels, sets --ing-panels-min-height to
     the tallest so tab switches don't cause layout jumps.
  ──────────────────────────────────────────────────────── */
  function initPanelHeights(section) {
    var panels = Array.from(section.querySelectorAll('.pdp-ing__panel'));
    if (!panels.length) return;

    var maxH = 0;
    panels.forEach(function (panel) {
      var wasHidden = panel.hasAttribute('hidden');
      var wasInactive = !panel.classList.contains('is-active');
      /* temporarily reveal to measure */
      if (wasHidden) panel.removeAttribute('hidden');
      if (wasInactive) {
        panel.style.visibility = 'hidden';
        panel.style.display = 'block';
      }
      maxH = Math.max(maxH, panel.scrollHeight);
      /* restore */
      if (wasInactive) {
        panel.style.visibility = '';
        panel.style.display = '';
      }
      if (wasHidden) panel.setAttribute('hidden', '');
    });

    if (maxH > 0) {
      section.style.setProperty('--ing-panels-min-height', maxH + 'px');
    }
  }

  /* ─────────────────────────────────────────────────────
     8. MOBILE ACCORDION (≤600px)
     Each category header expands to show 2-col ingredient grid.
     Uses exact scrollHeight for smooth, correctly-eased animation.
  ────────────────────────────────────────────────────── */
  function openAccContent(el) {
    /* Cancel any stale transitionend listener from a previous open (race condition guard) */
    if (el._accOpenListener) {
      el.removeEventListener('transitionend', el._accOpenListener);
      el._accOpenListener = null;
    }
    /* Force card animation to replay on rapid close→open */
    el.classList.remove('is-open');
    void el.offsetHeight;
    el.classList.add('is-open');
    el.style.maxHeight = el.scrollHeight + 'px';
    el._accOpenListener = function onEnd(e) {
      if (e.propertyName !== 'max-height') return;
      /* Only release max-height constraint if still open */
      if (el.classList.contains('is-open')) {
        el.style.maxHeight = 'none';
      }
      el.removeEventListener('transitionend', el._accOpenListener);
      el._accOpenListener = null;
    };
    el.addEventListener('transitionend', el._accOpenListener);
  }

  function closeAccContent(el) {
    /* Cancel any pending open transitionend before closing — prevents race condition */
    if (el._accOpenListener) {
      el.removeEventListener('transitionend', el._accOpenListener);
      el._accOpenListener = null;
    }
    /* Pin to current rendered height so transition animates from actual size */
    if (!el.style.maxHeight || el.style.maxHeight === 'none') {
      el.style.maxHeight = el.scrollHeight + 'px';
      void el.offsetHeight; /* force reflow */
    }
    el.style.maxHeight = '0';
    el.classList.remove('is-open');
  }

  function initMobileAccordion(section) {
    var headers = Array.from(section.querySelectorAll('.pdp-ing__acc-header'));
    if (!headers.length) return;

    headers.forEach(function (header) {
      header.addEventListener('click', function () {
        var isOpen = header.getAttribute('aria-expanded') === 'true';
        var content = header.closest('.pdp-ing__acc-item').querySelector('.pdp-ing__acc-content');

        /* close all others */
        headers.forEach(function (h) {
          if (h === header) return;
          h.setAttribute('aria-expanded', 'false');
          var c = h.closest('.pdp-ing__acc-item').querySelector('.pdp-ing__acc-content');
          if (c) closeAccContent(c);
        });

        /* toggle this one */
        if (isOpen) {
          header.setAttribute('aria-expanded', 'false');
          closeAccContent(content);
        } else {
          header.setAttribute('aria-expanded', 'true');
          openAccContent(content);
        }
      });
    });
  }

  /* ─────────────────────────────────────────────────────
     9. ACCORDION ENTRANCE — mirrors theme's fade-in-up
     Applies only on mobile (≤600px) via matchMedia check.
     Initial state set in JS so desktop is never affected.
  ────────────────────────────────────────────────────── */
  function initAccordionEntrance(section) {
    if (prefersReducedMotion()) return;
    if (!window.matchMedia('(max-width: 600px)').matches) return;

    var items = Array.from(section.querySelectorAll('.pdp-ing__acc-item'));
    if (!items.length) return;

    /* Set initial hidden state via JS (not CSS) so desktop is unaffected */
    items.forEach(function (item) {
      item.style.opacity = '0';
      item.style.transform = 'translateY(2rem)';
      item.style.transition = 'opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1), transform 0.65s cubic-bezier(0.22, 1, 0.36, 1)';
    });

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var item = entry.target;
        var idx = items.indexOf(item);
        /* Stagger: 0ms, 80ms, 160ms … for items visible together on load */
        item.style.transitionDelay = (idx * 80) + 'ms';
        /* Double rAF ensures initial state is painted before we start transition */
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            item.style.opacity = '1';
            item.style.transform = 'none';
          });
        });
        observer.unobserve(item);
      });
    }, { threshold: 0.08 });

    items.forEach(function (item) {
      observer.observe(item);
    });
  }

  /* ─────────────────────────────────────────────────────
     INIT — runs per section (supports multiple on page)
  ────────────────────────────────────────────────────── */
  function initSection(section) {
    /* wire tab buttons */
    section.querySelectorAll('.pdp-ing__nav-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        switchPanel(btn, section);
      });
    });

    initTabKeyboard(section);
    initPanelSwipe(section);
    initMobileNav(section);
    initMobileAccordion(section);
    initAccordionEntrance(section);
    initPanelHeights(section);
    initEntranceObserver(section);
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-section-type="pdp-ingredients"]').forEach(function (section) {
      initSection(section);
    });
  });

  /* Support Shopify section reload in the theme editor */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-section-type="pdp-ingredients"]');
    if (section) initSection(section);
  });
})();
